/**
 * AIInsightsPanel - Premium Insights Feed
 *
 * Premium fintech aesthetic - clean, minimal, professional.
 * Displays personalized insights as expert observations.
 */

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { AIInsight } from '../../../api/_schemas/journeyOutput.schema';

interface AIInsightsPanelProps {
  insights: AIInsight[];
  className?: string;
}

// Insight type icons (SVG)
const InsightIcons = {
  opportunity: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  ),
  warning: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  critical: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  tip: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  confirmation: (
    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" className="w-full h-full">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
};

// Insight type styles - emerald for opportunities, matching Projections theme
const INSIGHT_CONFIG = {
  opportunity: {
    icon: InsightIcons.opportunity,
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    textColor: 'text-emerald-700',
    titleColor: 'text-emerald-800',
    label: 'Opportunity',
    labelBg: 'bg-emerald-100 text-emerald-700',
  },
  warning: {
    icon: InsightIcons.warning,
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    iconBg: 'bg-amber-100',
    textColor: 'text-amber-700',
    titleColor: 'text-amber-800',
    label: 'Watch Out',
    labelBg: 'bg-amber-100 text-amber-700',
  },
  critical: {
    icon: InsightIcons.critical,
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    iconBg: 'bg-red-100',
    textColor: 'text-red-700',
    titleColor: 'text-red-800',
    label: 'Critical',
    labelBg: 'bg-red-100 text-red-700',
  },
  tip: {
    icon: InsightIcons.tip,
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    iconBg: 'bg-blue-100',
    textColor: 'text-blue-700',
    titleColor: 'text-blue-800',
    label: 'Pro Tip',
    labelBg: 'bg-blue-100 text-blue-700',
  },
  confirmation: {
    icon: InsightIcons.confirmation,
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
    textColor: 'text-emerald-600',
    titleColor: 'text-emerald-700',
    label: 'Good Move',
    labelBg: 'bg-emerald-100 text-emerald-600',
  },
} as const;

// Urgency styles - matching Projections theme
const URGENCY_STYLES = {
  immediate: { bg: 'bg-red-100', text: 'text-red-700', label: 'Act Now' },
  before_return: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'Before Return' },
  during_rnor: { bg: 'bg-emerald-100', text: 'text-emerald-700', label: 'During RNOR' },
  ongoing: { bg: 'bg-slate-100', text: 'text-slate-500', label: 'Ongoing' },
} as const;

// Individual insight card
interface InsightCardProps {
  insight: AIInsight;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
}

function InsightCard({ insight, index, isExpanded, onToggle }: InsightCardProps) {
  const insightType = insight.type ?? 'tip';
  const config = INSIGHT_CONFIG[insightType] ?? INSIGHT_CONFIG.tip;
  const urgencyKey = insight.urgency;
  const urgency = urgencyKey && URGENCY_STYLES[urgencyKey] ? URGENCY_STYLES[urgencyKey] : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.08 }}
      className={cn(
        'rounded-lg border overflow-hidden transition-all cursor-pointer',
        config.bgColor,
        config.borderColor,
        isExpanded && 'ring-1 ring-slate-300'
      )}
      onClick={onToggle}
    >
      <div className="p-4">
        <div className="flex items-start gap-3">
          {/* Icon */}
          <div className={cn(
            'w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0',
            config.iconBg, config.textColor
          )}>
            {config.icon}
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Labels */}
            <div className="flex items-center gap-2 mb-1.5 flex-wrap">
              <span className={cn(
                'px-2 py-0.5 rounded text-xs font-medium',
                config.labelBg
              )}>
                {config.label}
              </span>
              {urgency && (
                <span className={cn(
                  'px-2 py-0.5 rounded text-xs font-medium',
                  urgency.bg, urgency.text
                )}>
                  {urgency.label}
                </span>
              )}
              {insight.actionRequired && (
                <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-200 text-slate-600">
                  Action Required
                </span>
              )}
            </div>

            {/* Title */}
            <h4 className={cn('font-medium text-sm mb-1', config.titleColor)}>
              {insight.title}
            </h4>

            {/* Preview or full insight */}
            <p className={cn(
              'text-sm',
              config.textColor,
              !isExpanded && 'line-clamp-2'
            )}>
              {insight.insight}
            </p>

            {/* Expanded content */}
            <AnimatePresence>
              {isExpanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden"
                >
                  <div className="mt-3 pt-3 border-t border-slate-200 space-y-2">
                    {insight.specificAmount && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Potential Impact:</span>
                        <span className="font-medium text-slate-700">{insight.specificAmount}</span>
                      </div>
                    )}
                    {insight.relatedAsset && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Related to:</span>
                        <span className="text-sm text-slate-600">{insight.relatedAsset}</span>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Expand indicator */}
          <motion.div
            animate={{ rotate: isExpanded ? 180 : 0 }}
            className="text-slate-400 flex-shrink-0"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </motion.div>
        </div>
      </div>
    </motion.div>
  );
}

const DEFAULT_VISIBLE_COUNT = 2;

export function AIInsightsPanel({ insights, className }: AIInsightsPanelProps) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [filter, setFilter] = useState<AIInsight['type'] | 'all'>('all');
  const [showAll, setShowAll] = useState(false);

  // Sort insights by priority
  const priorityOrder = { critical: 0, opportunity: 1, warning: 2, tip: 3, confirmation: 4 };
  const sortedInsights = [...insights].sort(
    (a, b) => (priorityOrder[a.type] ?? 4) - (priorityOrder[b.type] ?? 4)
  );

  // Filter insights
  const filteredInsights = filter === 'all'
    ? sortedInsights
    : sortedInsights.filter(i => i.type === filter);

  const visibleInsights = showAll
    ? filteredInsights
    : filteredInsights.slice(0, DEFAULT_VISIBLE_COUNT);
  const hiddenCount = filteredInsights.length - visibleInsights.length;

  // Count by type
  const countByType = insights.reduce((acc, insight) => {
    acc[insight.type] = (acc[insight.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className={cn('space-y-4', className)}>
      {/* Section Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center text-white">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Personalized Insights</h3>
            <p className="text-sm text-slate-500">
              {insights.length} observation{insights.length !== 1 ? 's' : ''} for your profile
            </p>
          </div>
        </div>
      </div>

      {/* Filter pills */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter('all')}
          className={cn(
            'px-3 py-1.5 rounded text-sm font-medium transition-all',
            filter === 'all'
              ? 'bg-emerald-600 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          )}
        >
          All ({insights.length})
        </button>
        {Object.entries(INSIGHT_CONFIG).map(([type, config]) => {
          const count = countByType[type] || 0;
          if (count === 0) return null;
          return (
            <button
              key={type}
              onClick={() => setFilter(type as AIInsight['type'])}
              className={cn(
                'px-3 py-1.5 rounded text-sm font-medium transition-all',
                filter === type
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              )}
            >
              {config.label} ({count})
            </button>
          );
        })}
      </div>

      {/* Insights list */}
      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {visibleInsights.map((insight, index) => (
            <InsightCard
              key={insight.id}
              insight={insight}
              index={index}
              isExpanded={expandedId === insight.id}
              onToggle={() => setExpandedId(expandedId === insight.id ? null : insight.id)}
            />
          ))}
        </AnimatePresence>
      </div>

      {/* Show more button */}
      {filteredInsights.length > DEFAULT_VISIBLE_COUNT && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowAll(!showAll)}
          className="w-full py-2.5 px-4 rounded-lg border border-slate-200
                     text-slate-600 font-medium hover:bg-slate-50
                     transition-all flex items-center justify-center gap-2"
        >
          {showAll ? (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
              </svg>
              Show less
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Show {hiddenCount} more
            </>
          )}
        </motion.button>
      )}

      {/* Empty state */}
      {filteredInsights.length === 0 && (
        <div className="text-center py-8 text-slate-500">
          <p>No insights match this filter.</p>
          <button
            onClick={() => setFilter('all')}
            className="text-slate-700 hover:underline mt-2"
          >
            Show all insights
          </button>
        </div>
      )}

      {/* Trust indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="flex items-center justify-center gap-2 text-xs text-slate-400 pt-2"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
        <span>Generated from your profile and current tax regulations</span>
      </motion.div>
    </div>
  );
}

export default AIInsightsPanel;
